import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { APIError } from '~services/Errors';

import { getCampaignGroups, getCampaignGroupsByNextUrl } from './api';
import { CampaignGroup, CampaignGroupResponse } from './domain';

type Options = {
  shouldFetch?: boolean;
};

const useCampaignGroupSearch = (campaignId: number, search: string, options: Options = {}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [list, setList] = useState<CampaignGroup[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const { shouldFetch = true } = options;

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError('');

      let resp: CampaignGroupResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getCampaignGroupsByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(e instanceof APIError ? e.message : 'Unexpected Error');
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.list]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  useEffect(() => {
    setList([]);
  }, [search, shouldFetch]);

  useEffect(() => {
    const load = async (search: string) => {
      setLoading(true);
      setError('');

      let resp: CampaignGroupResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        // resp = await getCampaigns(search, filters, accessFilterId, axiosCancelRef.current);
        resp = await getCampaignGroups({
          campaignId,
          search,
          cancelToken: axiosCancelRef.current,
        });
      } catch (e) {
        setError(e instanceof APIError ? e.message : 'Unexpected Error');
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.list]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load(search);
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, shouldFetch]);

  return { loading, error, list, hasMore, intersectionObserverRef };
};

export default useCampaignGroupSearch;
